// FRONT-1698: data layer for the composer model picker (flag
// `eddy.model-picker-composer`). Four server resources:
//   - the person's picker prefs   GET/PUT /api/llm/model-picker
//   - this conversation's model   GET/PUT /api/llm/chat-model/:sessionId
//   - the standing (no-session)   GET/PUT /api/llm/chat-model/default
//   - the person's thinking depth GET/PUT /api/llm/thinking (the slider; the
//     same stored value as Settings → Models' Thinking)
//
// Optimistic writes follow one rule: a pick's success/rollback only touch the
// cache while it STILL HOLDS that pick's own optimistic object. Picks for one
// target share a mutation `scope` so the PUTs reach the server in click order,
// but `onMutate` (and so the optimistic write) runs immediately — meaning a
// later click has already overwritten the cache by the time an earlier PUT
// settles, and an unguarded rollback/success would clobber the newer value.
// Nothing here ever calls `reset()` on a mutation that may still be pending.

import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type MutationOptions,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { llmKeys } from '~/lib/query-keys';
import type { ThinkingLevel } from '~/lib/types/llm';
import {
  MODEL_PICKER_COMPOSER_FLAG,
  resolveFeatureFlag,
  type FeatureFlagsResponse,
} from '~/hooks/queries/use-feature-flags';
import { ensureChatModelMode } from '~/hooks/queries/use-chat-model-mode';
import type { ChatModelMode } from '~/lib/chat-model-mode';
import {
  applyPrefsPatch,
  modelName,
  type ChatModelState,
  type PickerPrefs,
} from '~/features/chat/model-picker-logic';

const STALE_TIME_MS = 30_000;

export const modelPickerKeys = {
  /** Prefix of everything in this file. */
  all: () => ['llm', 'model-picker'] as const,
  prefs: () => ['llm', 'model-picker', 'prefs'] as const,
  /** Prefix of every per-session model entry (invalidate all sessions). */
  chatLists: () => ['llm', 'chat-model'] as const,
  chat: (sessionId: string) => ['llm', 'chat-model', 'session', sessionId] as const,
  standing: () => ['llm', 'chat-model', 'default'] as const,
  /** Prefix of every thinking entry (invalidate all chats' sliders). */
  thinkingAll: () => ['llm', 'thinking'] as const,
  /** The level a chat's next turn uses; no sessionId = the person's own value. */
  thinking: (sessionId?: string) => ['llm', 'thinking', sessionId ?? ''] as const,
};

const prefsMutationKey = ['llm', 'model-picker', 'update'] as const;
const pickMutationRoot = ['llm', 'chat-model', 'pick'] as const;
const pickMutationKey = (sessionId: string | undefined) =>
  ['llm', 'chat-model', 'pick', sessionId ?? 'default'] as const;
const thinkingMutationKey = ['llm', 'thinking', 'set'] as const;

// ---------------------------------------------------------------- queries

export function useModelPickerPrefs(enabled = true) {
  return useQuery<PickerPrefs>({
    queryKey: modelPickerKeys.prefs(),
    queryFn: () => api.get<PickerPrefs>('/api/llm/model-picker'),
    staleTime: STALE_TIME_MS,
    enabled,
  });
}

/**
 * 404/403 resolve to null (a foreign-owned id, FRONT-1412 posture): a model
 * lookup must never take the composer down; the button then shows the default.
 */
export async function fetchChatModel(sessionId: string): Promise<ChatModelState | null> {
  try {
    return await api.get<ChatModelState>(`/api/llm/chat-model/${encodeURIComponent(sessionId)}`);
  } catch (err) {
    const status = (err as { status?: number }).status;
    if (status === 404 || status === 403) return null;
    throw err;
  }
}

export function useChatModel(sessionId: string | undefined, enabled = true) {
  return useQuery<ChatModelState | null>({
    queryKey: modelPickerKeys.chat(sessionId ?? ''),
    queryFn: () => fetchChatModel(sessionId!),
    staleTime: STALE_TIME_MS,
    enabled: enabled && !!sessionId,
  });
}

interface StandingState {
  model: string | null;
}

export function useStandingChatModel(enabled = true) {
  return useQuery<StandingState>({
    queryKey: modelPickerKeys.standing(),
    queryFn: () => api.get<StandingState>('/api/llm/chat-model/default'),
    staleTime: STALE_TIME_MS,
    enabled,
  });
}

// ------------------------------------------------------------- mutations

interface PrefsPatch {
  order?: string[];
  starred?: string[];
}
interface PrefsCtx {
  previous: PickerPrefs | undefined;
  optimistic: PickerPrefs | undefined;
}

export function updatePrefsOptions(
  qc: QueryClient,
): MutationOptions<PickerPrefs, Error, PrefsPatch, PrefsCtx> {
  const key = modelPickerKeys.prefs();
  return {
    mutationKey: prefsMutationKey,
    scope: { id: 'model-picker-prefs' },
    mutationFn: (patch) => api.put<PickerPrefs>('/api/llm/model-picker', patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<PickerPrefs>(key);
      if (previous) qc.setQueryData(key, applyPrefsPatch(previous, patch));
      // Read back what the cache actually stores: structural sharing may hand
      // back a copy, and the ownership checks below compare by reference.
      return { previous, optimistic: previous ? qc.getQueryData<PickerPrefs>(key) : undefined };
    },
    onSuccess: (server, _patch, ctx) => {
      // Only a still-current optimistic value is replaced; a newer click's
      // value must survive this (older) response.
      if (ctx?.optimistic && qc.getQueryData(key) === ctx.optimistic) qc.setQueryData(key, server);
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.optimistic && qc.getQueryData(key) === ctx.optimistic) {
        if (ctx.previous) qc.setQueryData(key, ctx.previous);
        else void qc.invalidateQueries({ queryKey: key });
      }
      toast.error("Couldn't save your model list. Try again.");
    },
    onSettled: () => {
      // Self still counts as pending here; refetch only when nothing newer is queued.
      if (qc.isMutating({ mutationKey: prefsMutationKey }) <= 1) {
        void qc.invalidateQueries({ queryKey: key });
      }
    },
  };
}

export function useUpdateModelPickerPrefs() {
  const qc = useQueryClient();
  return useMutation(updatePrefsOptions(qc));
}

/**
 * FRONT-3249: make a model the person's own default chat model — the one a
 * conversation without a pick runs on ("Default" in the Hub table). Writes the
 * personal chat-model setting Settings › Models also edits, then refreshes
 * everything that shows the default.
 */
export function useSetDefaultChatModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (model: string) => api.put('/api/llm/settings', { params: { model } }),
    onError: () => toast.error("Couldn't change your default model. Try again."),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: modelPickerKeys.prefs() });
      void qc.invalidateQueries({ queryKey: modelPickerKeys.chatLists() });
      void qc.invalidateQueries({ queryKey: llmKeys.settings() });
    },
  });
}

type PickTarget = ChatModelState | StandingState;
interface PickCtx {
  previous: PickTarget | null | undefined;
  optimistic: PickTarget;
}

function optimisticFor(
  sessionId: string | undefined,
  model: string | null,
  prefs: PickerPrefs | undefined,
  previous: PickTarget | null | undefined,
): PickTarget {
  if (!sessionId) return { model };
  if (model) {
    return {
      model,
      active: { id: model, displayName: modelName(prefs, model) ?? model, source: 'pick' },
    };
  }
  const d = prefs?.defaultModel ?? null;
  const prevActive = previous && 'active' in previous ? previous.active : null;
  return {
    model: null,
    active: d ? { id: d.id, displayName: d.displayName, source: 'default' } : prevActive,
  };
}

/**
 * Pick (or clear, with null) the model for a conversation; with no sessionId
 * it writes the standing pick that seeds the next new chat.
 */
export function pickChatModelOptions(
  qc: QueryClient,
  sessionId: string | undefined,
): MutationOptions<PickTarget, Error, string | null, PickCtx> {
  const key = sessionId ? modelPickerKeys.chat(sessionId) : modelPickerKeys.standing();
  const mutationKey = pickMutationKey(sessionId);
  return {
    mutationKey,
    scope: { id: `chat-model:${sessionId ?? 'default'}` },
    mutationFn: (model) =>
      api.put<PickTarget>(
        sessionId
          ? `/api/llm/chat-model/${encodeURIComponent(sessionId)}`
          : '/api/llm/chat-model/default',
        { model },
      ),
    onMutate: async (model) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<PickTarget | null>(key);
      const prefs = qc.getQueryData<PickerPrefs>(modelPickerKeys.prefs());
      qc.setQueryData(key, optimisticFor(sessionId, model, prefs, previous));
      // The stored reference (structural sharing may copy), compared below.
      const optimistic = qc.getQueryData<PickTarget>(key)!;
      return { previous, optimistic };
    },
    onSuccess: (server, _model, ctx) => {
      if (ctx && qc.getQueryData(key) === ctx.optimistic) qc.setQueryData(key, server);
    },
    onError: (_err, _model, ctx) => {
      if (ctx && qc.getQueryData(key) === ctx.optimistic) {
        if (ctx.previous !== undefined) qc.setQueryData(key, ctx.previous);
        else void qc.invalidateQueries({ queryKey: key });
      }
      toast.error("Couldn't switch the model. Try again.");
    },
    onSettled: () => {
      // A pick also moves the person's standing model, which every UNCLAIMED
      // chat's button shows — so once no pick is in flight anywhere, EVERY
      // active-model entry (all chats and the standing one) is refetched, not
      // just this chat's. While another pick is still pending its own settle
      // does that sweep; only this chat's entry is refreshed meanwhile, and
      // never over a newer optimistic value still in flight.
      if (qc.isMutating({ mutationKey: pickMutationRoot }) <= 1) {
        void qc.invalidateQueries({ queryKey: modelPickerKeys.chatLists() });
      } else if (qc.isMutating({ mutationKey }) <= 1) {
        void qc.invalidateQueries({ queryKey: key });
      }
      if (qc.isMutating({ mutationKey: prefsMutationKey }) === 0) {
        void qc.invalidateQueries({ queryKey: modelPickerKeys.prefs() });
      }
      if (sessionId && qc.isMutating({ mutationKey: pickMutationKey(undefined) }) === 0) {
        void qc.invalidateQueries({ queryKey: modelPickerKeys.standing() });
      }
    },
  };
}

export function usePickChatModel(sessionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation(pickChatModelOptions(qc, sessionId));
}

// ------------------------------------------------------------ thinking

export interface ThinkingState {
  /** Null is Auto. */
  level: ThinkingLevel | null;
  /** `chat`: a level Eddy set for this chat itself, which wins over the person's own. */
  source?: 'chat' | 'person';
}

/**
 * The level the chat's next turn uses — the person's own, unless Eddy set one
 * for this chat ("think harder"), which wins at turn time and so here too.
 */
export function useThinkingLevel(sessionId?: string, enabled = true) {
  return useQuery<ThinkingState>({
    queryKey: modelPickerKeys.thinking(sessionId),
    queryFn: () =>
      api.get<ThinkingState>(
        sessionId ? `/api/llm/thinking?sessionId=${encodeURIComponent(sessionId)}` : '/api/llm/thinking',
      ),
    staleTime: STALE_TIME_MS,
    enabled,
  });
}

interface ThinkingCtx {
  previous: ThinkingState | undefined;
  optimistic: ThinkingState;
}

/**
 * Move the person's thinking slider. With a `sessionId`, the server also drops
 * that chat's own thinking override, so the next turn there uses the slider.
 * Same ownership rule as a pick: success/rollback only touch a cache that still
 * holds this move's own optimistic value.
 */
export function setThinkingOptions(
  qc: QueryClient,
  sessionId: string | undefined,
): MutationOptions<ThinkingState, Error, ThinkingLevel | null, ThinkingCtx> {
  const key = modelPickerKeys.thinking(sessionId);
  return {
    mutationKey: thinkingMutationKey,
    scope: { id: 'thinking' },
    mutationFn: (level) => api.put<ThinkingState>('/api/llm/thinking', { level, ...(sessionId ? { sessionId } : {}) }),
    onMutate: async (level) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ThinkingState>(key);
      qc.setQueryData<ThinkingState>(key, { level, source: 'person' });
      return { previous, optimistic: qc.getQueryData<ThinkingState>(key)! };
    },
    onSuccess: (server, _level, ctx) => {
      if (ctx && qc.getQueryData(key) === ctx.optimistic) qc.setQueryData(key, server);
    },
    onError: (_err, _level, ctx) => {
      if (ctx && qc.getQueryData(key) === ctx.optimistic) {
        if (ctx.previous) qc.setQueryData(key, ctx.previous);
        else void qc.invalidateQueries({ queryKey: key });
      }
      toast.error("Couldn't change the thinking level. Try again.");
    },
    onSettled: () => {
      if (qc.isMutating({ mutationKey: thinkingMutationKey }) <= 1) {
        // Every chat's slider: the person's level is what each of them shows.
        void qc.invalidateQueries({ queryKey: modelPickerKeys.thinkingAll() });
        // Settings → Models shows the same value.
        void qc.invalidateQueries({ queryKey: llmKeys.settings() });
      }
    },
  };
}

export function useSetThinkingLevel(sessionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation(setThinkingOptions(qc, sessionId));
}

// ------------------------------------------------------ request builder

/** Cached read of the flag for non-React code; unloaded cache reads as off. */
export function isModelPickerFlagOn(qc: QueryClient): boolean {
  const data = qc.getQueryData<FeatureFlagsResponse>(['feature-flags']);
  return resolveFeatureFlag(MODEL_PICKER_COMPOSER_FLAG, false, data, false, false);
}

const PENDING_PICK_WAIT_CAP_MS = 10_000;

/**
 * Resolves once no pick save for this conversation (or the standing pick), and
 * no thinking-slider save, is in flight. A quick pick or slider move followed
 * at once by Send must not race: the send's request would otherwise reach the
 * server before the PUT and run on the OLD model or depth. Capped, so a hung
 * save can delay a send but never block it forever.
 */
export function waitForPendingPicks(qc: QueryClient, sessionId: string): Promise<void> {
  const pending = () =>
    qc.isMutating({ mutationKey: pickMutationKey(sessionId) }) +
    qc.isMutating({ mutationKey: pickMutationKey(undefined) }) +
    qc.isMutating({ mutationKey: thinkingMutationKey });
  if (pending() === 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const cache = qc.getMutationCache();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = cache.subscribe(() => {
      if (pending() === 0) done();
    });
    function done() {
      unsubscribe();
      if (timer) clearTimeout(timer);
      resolve();
    }
    timer = setTimeout(done, PENDING_PICK_WAIT_CAP_MS);
  });
}

/** After a turn completes: re-read what the server says the active model is. */
export function refreshActiveChatModel(qc: QueryClient, sessionId: string): void {
  void qc.invalidateQueries({ queryKey: modelPickerKeys.chat(sessionId) });
}

/**
 * The `modelMode` a turn's request body carries. Null for a direct-agent chat
 * (a pinned agent runs on its own model) and, with the picker flag on, always
 * null: the pick replaces Fast/Thoughtful, and a stale pill mode must not
 * ride along — and a pick still being saved is awaited first, so the turn runs
 * on it. Otherwise the session's mode, never throwing (the lookup is
 * optional and must not kill a send).
 */
export async function resolveTurnModelMode(
  qc: QueryClient,
  sessionId: string,
  isDirectAgent: boolean,
): Promise<ChatModelMode | null> {
  if (isDirectAgent) return null;
  if (isModelPickerFlagOn(qc)) {
    await waitForPendingPicks(qc, sessionId);
    return null;
  }
  try {
    return await ensureChatModelMode(qc, sessionId);
  } catch (err) {
    console.warn('[chat] chat-mode lookup failed, sending without one:', err);
    return null;
  }
}
